import { useMutation } from '@tanstack/react-query';
import React, { useEffect, useState, type UIEvent } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/api/client';
import { API_TECHNICAL_SUPPORT } from '@/api/endpoints';
import { useUserContext } from '@/auth/UserContext';
import { Button } from '@/components/Button';
import { ArrowLeft } from '@/components/Icons/ArrowLeft';
import { Close } from '@/components/Icons/Close';
import { User } from '@/components/Icons/User';
import { Modal } from '@/components/Modal';
import {
  ButtonType,
  ButtonVariant,
  ContactSupportFieldErrorId,
  ModalSize,
  Size,
} from '@/types/components';
import { ErrorMessage, logRequestError } from '@/utils/errors';
import { getRoleLabel } from '@/utils/person';
import { getFullName, trimString } from '@/utils/text';
import {
  EMAIL_REGEX,
  hasAtLeastTwoWords,
  hasValue,
  isValidPhone,
} from '@/utils/validation';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onError: () => void;
}

interface ContactSupportFormValues {
  email: string;
  phone: string;
  message: string;
}

interface TechnicalSupportRequest {
  senderEmailAddress: string;
  senderPhoneNumber: string;
  requestText: string;
}

const COMPACT_TITLE_FADE_RANGE = 48;
const { Email, Phone, Message } = ContactSupportFieldErrorId;

export const ContactSupportModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSuccess,
  onError,
}) => {
  const { t } = useTranslation();
  const { user, currentProfile } = useUserContext();
  const [scrollProgress, setScrollProgress] = useState(0);

  const userFullName = getFullName(user);
  const userRole = getRoleLabel(user?.personType);
  const schoolName = currentProfile?.user.schoolName;
  const defaultValues: ContactSupportFormValues = {
    email: user?.emailAddress ?? '',
    phone: user?.phoneNumber ?? '',
    message: '',
  };

  const validateMessage = (value: string) => {
    if (!hasValue(value)) {
      return t('contactSupport.messageField.missingError');
    }

    if (!hasAtLeastTwoWords(value)) {
      return t('contactSupport.messageField.tooShortError');
    }

    return true;
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, submitCount },
  } = useForm<ContactSupportFormValues>({
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const { mutateAsync: submitSupportRequest, isPending } = useMutation({
    mutationFn: (payload: TechnicalSupportRequest) =>
      apiClient.post<unknown, unknown, TechnicalSupportRequest>(
        API_TECHNICAL_SUPPORT,
        payload,
      ),
  });

  const handleClose = () => {
    reset(defaultValues);
    setScrollProgress(0);
    onClose();
  };

  const onSubmit = async (values: ContactSupportFormValues) => {
    try {
      await submitSupportRequest({
        senderEmailAddress: values.email,
        senderPhoneNumber: values.phone,
        requestText: values.message,
      });
      handleClose();
      onSuccess();
    } catch (error) {
      logRequestError(ErrorMessage.TechnicalSupport, error);
      onError();
    }
  };

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const next = Math.min(
      event.currentTarget.scrollTop / COMPACT_TITLE_FADE_RANGE,
      1,
    );
    setScrollProgress(next);
  };

  useEffect(() => {
    const firstError = Object.values(ContactSupportFieldErrorId)
      .map((id) => document.getElementById(id))
      .find(Boolean);
    firstError?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [submitCount]);

  return (
    <Modal
      isOpen={isOpen}
      label={t('contactSupport.title')}
      size={ModalSize.Large}
      onClose={handleClose}
    >
      <div className="relative flex shrink-0 items-center bg-background px-4 pt-[calc(24px+env(safe-area-inset-top))] pb-3 md:justify-between md:px-5 md:pt-5 md:pb-3">
        <button
          type="button"
          onClick={handleClose}
          aria-label={t('global.back')}
          className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-foreground md:hidden"
        >
          <ArrowLeft />
        </button>
        <h2
          style={{ opacity: scrollProgress }}
          className="absolute left-1/2 -translate-x-1/2 text-callout-16 text-foreground md:hidden"
        >
          {t('contactSupport.title')}
        </h2>
        <h2 className="hidden text-headline-20 text-foreground md:block">
          {t('contactSupport.title')}
        </h2>
        <button
          type="button"
          onClick={handleClose}
          aria-label={t('global.close')}
          className="hidden size-6 shrink-0 cursor-pointer items-center justify-center text-foreground md:flex"
        >
          <Close className="size-4" />
        </button>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-1 flex-col overflow-hidden"
      >
        <div
          onScroll={handleScroll}
          className="flex flex-1 flex-col gap-6 overflow-y-auto px-4 pb-4 md:px-5"
        >
          <h2
            style={{ opacity: 1 - scrollProgress }}
            className="text-title-36 text-foreground transition-opacity duration-[50ms] md:hidden"
          >
            {t('contactSupport.title')}
          </h2>
          <p className="text-body-14 text-muted-foreground">
            {t('contactSupport.message')}{' '}
            <a
              href={`tel:${t('contactSupport.supportPhone')}`}
              className="text-primary"
            >
              {t('contactSupport.supportPhone')}
            </a>
          </p>
          {userFullName && (
            <div className="flex items-start gap-3 border-b border-border pb-5">
              <User className="size-6 shrink-0 text-foreground" />
              <div className="flex flex-col">
                <p className="text-body-16 text-foreground capitalize">
                  {userFullName}
                  {!!userRole.length && ` (${userRole})`}
                </p>
                {schoolName && (
                  <p className="text-caption-12 text-muted-foreground">
                    {schoolName}
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label
                htmlFor="contact-support-email"
                className="text-body-16 text-foreground"
              >
                {t('contactSupport.email.label')}
              </label>
              <input
                id="contact-support-email"
                type="email"
                autoComplete="email"
                placeholder={t('contactSupport.email.placeholder')}
                className="h-14 rounded-xl md:h-12 bg-on-primary-surface px-4 text-body-16 text-foreground placeholder:text-subtle-foreground outline-none"
                {...register('email', {
                  setValueAs: trimString,
                  required: t('contactSupport.email.missingError'),
                  pattern: {
                    value: EMAIL_REGEX,
                    message: t('contactSupport.email.invalidError'),
                  },
                })}
              />
              {errors.email && (
                <p id={Email} className="text-body-14 text-destructive">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label
                htmlFor="contact-support-phone"
                className="text-body-16 text-foreground"
              >
                {t('contactSupport.phone.label')}
              </label>
              <input
                id="contact-support-phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder={t('contactSupport.phone.placeholder')}
                className="h-14 rounded-xl md:h-12 bg-on-primary-surface px-4 text-body-16 text-foreground placeholder:text-subtle-foreground outline-none"
                {...register('phone', {
                  setValueAs: trimString,
                  validate: (value) =>
                    isValidPhone(value) ||
                    t('contactSupport.phone.missingError'),
                })}
              />
              {errors.phone && (
                <p id={Phone} className="text-body-14 text-destructive">
                  {errors.phone.message}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <textarea
              placeholder={t('contactSupport.messageField.label')}
              rows={6}
              className="min-h-[180px] resize-none rounded-xl border border-divider bg-on-primary-surface px-4 py-4 text-body-16 text-foreground placeholder:text-subtle-foreground outline-none"
              {...register('message', { validate: validateMessage })}
            />
            {errors.message && (
              <p id={Message} className="text-body-14 text-destructive">
                {errors.message.message}
              </p>
            )}
          </div>
        </div>

        <div className="flex shrink-0 gap-3 bg-background px-4 pt-4 pb-[calc(24px+env(safe-area-inset-bottom))] md:p-5">
          <Button
            variant={ButtonVariant.Primary}
            size={Size.Large}
            type={ButtonType.Submit}
            isLoading={isPending}
            className="flex-1"
          >
            {t('contactSupport.submit')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
